// 智慧客服 店家政策/FAQ editor (PETPRO_CONCIERGE_SPEC.md C1).
// These entries are the LINE concierge's ONLY source for policy answers —
// no entry, no answer (the bot hands off to staff instead of guessing).

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircleQuestion, Plus, Trash2 } from 'lucide-react'
import { concierge, type KbEntry } from './api/client'
import { Button } from './components/ui/button'
import { Card, CardContent } from './components/ui/card'
import { Input } from './components/ui/input'
import { Label } from './components/ui/label'
import { Switch } from './components/ui/switch'
import { toast } from './components/ui/use-toast'

export default function PoliciesPanel() {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<KbEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    concierge
      .listKb()
      .then((rows) => alive && setEntries(rows))
      .catch(() => alive && toast({ description: t('policies.loadFailed') }))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [t])

  const add = async () => {
    if (question.trim().length < 2 || answer.trim().length < 2) return
    setSaving(true)
    try {
      const created = await concierge.createKb({
        question: question.trim(),
        answer: answer.trim(),
      })
      setEntries((prev) => [created, ...prev])
      setQuestion('')
      setAnswer('')
      toast({ description: t('policies.added') })
    } catch {
      toast({ description: t('policies.saveFailed') })
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (entry: KbEntry) => {
    const next = !entry.is_active
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, is_active: next } : e)),
    )
    try {
      await concierge.updateKb(entry.id, { is_active: next })
    } catch {
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, is_active: !next } : e)),
      )
      toast({ description: t('policies.saveFailed') })
    }
  }

  const remove = async (entry: KbEntry) => {
    if (!window.confirm(t('policies.deleteConfirm'))) return
    try {
      await concierge.deleteKb(entry.id)
      setEntries((prev) => prev.filter((e) => e.id !== entry.id))
    } catch {
      toast({ description: t('policies.saveFailed') })
    }
  }

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center gap-2">
        <MessageCircleQuestion className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">{t('policies.title')}</h2>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {t('policies.subtitle')}
      </p>
      <Card>
        <CardContent className="space-y-4 p-5">
          {/* Add form */}
          <div className="space-y-2">
            <Label htmlFor="kb-q">{t('policies.questionLabel')}</Label>
            <Input
              id="kb-q"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('policies.questionPlaceholder')}
            />
            <Label htmlFor="kb-a">{t('policies.answerLabel')}</Label>
            <textarea
              id="kb-a"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t('policies.answerPlaceholder')}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button size="sm" onClick={add} disabled={saving}>
              <Plus className="mr-1 size-4" />
              {t('policies.addButton')}
            </Button>
          </div>

          {/* Entries */}
          {loading ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {t('loading')}
            </div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {t('policies.empty')}
            </div>
          ) : (
            <ul className="divide-y">
              {entries.map((e) => (
                <li key={e.id} className="flex items-start gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{e.question}</div>
                    <div className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">
                      {e.answer}
                    </div>
                  </div>
                  <Switch
                    checked={e.is_active}
                    onCheckedChange={() => toggle(e)}
                    aria-label={t('policies.activeToggle')}
                  />
                  <button
                    onClick={() => remove(e)}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                    aria-label={t('policies.delete')}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
